import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './HenaketDropdown.module.css';
import { normalize } from '../utils/format';
import { useSearchHighlight } from '../lib/searchHighlight';
// 0.36.278 — Both thresholds are platform-wide admin settings now.
// Defaults (7 / 15) live in dropdownThresholds.js; admin changes
// hydrate at App mount from /api/public/settings.
import { getEffectiveInlineThreshold, getSearchThreshold } from '../utils/dropdownThresholds';
import FormRadioGroup from './FormRadioGroup';

/**
 * HenaketDropdown — fully-controlled searchable dropdown.
 *
 * Replaces native <select> in places that need full Henaket
 * consistency in the open state too. Native <select>'s open state
 * is OS-rendered and cannot be styled — this component renders its
 * own panel so colors, font, separators, hover, and selection all
 * match Henaket tokens.
 *
 * Focus model (0.35.14):
 *   "Only ONE focus indicator visible at a time across the whole
 *   dropdown." Mutually-exclusive states:
 *
 *   - optionHighlightActive = true: the highlighted option shows a
 *     magenta inset border. Search input does NOT have focus.
 *   - optionHighlightActive = false: the user has clicked into the
 *     search input. Search shows its normal focus ring (via global
 *     *:focus-visible). NO option shows the magenta border, even
 *     the selected one (which stays bold).
 *
 * Transitions:
 *   - Panel opens → highlightActive = true, search NOT autofocused
 *   - Search input gains focus (click or Tab) → highlightActive = false
 *   - Mouse hover on option → search is blurred, highlightActive = true
 *   - Arrow keys (any source) → highlightActive = true, highlight moves
 *
 * Result: hover or keyboard-navigate produces ONE magenta border on
 * the active row. Clicking the search field produces ONE magenta ring
 * on the search input. Never both.
 *
 * Keyboard:
 *   ↓/↑    — move highlight (works from trigger OR search)
 *   Enter  — select highlighted option
 *   Esc    — close without selecting
 *   Tab    — close, advance focus naturally
 */

/* 0.36.278 — SEARCH_THRESHOLD is no longer a module constant; both
 * thresholds (search visibility, inline expansion) are admin-tunable
 * platform settings. Read at render time via getSearchThreshold() /
 * getInlineThreshold() so a settings change picks up after the next
 * App-mount hydration (same behavior as the date/time format
 * settings). */

const ChevronDown = () => (
  <svg
    className={styles.chevron}
    viewBox="0 0 10 10"
    width="12"
    height="12"
    aria-hidden="true"
  >
    <path d="M 0 2 L 10 2 L 5 8 Z" fill="currentColor" />
  </svg>
);

const SearchIcon = () => (
  <svg
    /* 0.36.277 — className dropped; icon is now centered via its
       flex parent `.searchIconWrap` instead of being absolute-
       positioned at left:10 inside the search input field. */
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M 11 11 L 15 15" />
  </svg>
);

export default function HenaketDropdown({
  value,
  onChange,
  // 0.45.33 - Colour the search text's matches in the options (the
  // shared search highlight). Off by default; the dropdown element's
  // "Highlight matches" setting turns it on.
  highlightMatches = false,
  highlightColor = null,
  options = [],
  placeholder = '',
  searchPlaceholder = 'Search',
  noMatchesLabel = 'No matches',
  required = false,
  disabled = false,
  name,
  id,
  ariaInvalid = false,
  // 0.36.560 — Explicit error state. When true, the trigger button
  // gets the canonical `.inputError` styling (2px red border) so the
  // dropdown matches other error-state inputs across the app.
  // Distinct from `ariaInvalid` (ARIA-only, no visual effect).
  error = false,
  /* 0.36.278 — Some call sites may want to FORCE the dropdown UI
   * regardless of the inline-expansion threshold (e.g. a status filter
   * that needs to fit a tight toolbar). Default behavior is to honor
   * the platform setting; admin can also globally disable expansion
   * by setting inline_threshold to 0. */
  disableAutoExpand = false,
  /* 0.36.278 — When set, this value drives the inline-expansion
   * decision instead of the route-effective threshold. Used by the
   * form preview (which renders inside /admin/* but should show what
   * the CITIZEN will see, not the admin's always-dropdown behavior).
   * The preview passes the raw platform threshold here so the
   * inline-expansion check matches the citizen-facing render.
   * 0.36.281 — Also drives the per-element override now: the form
   * editor lets admin pin a specific dropdown's threshold via a
   * field setting; that value flows through here. */
  inlineThreshold: inlineThresholdProp,
  /* 0.36.281 — Per-element search-threshold override. Same precedence
   * as inlineThreshold: when defined, takes precedence over the
   * platform setting from getSearchThreshold(). */
  searchThreshold: searchThresholdProp,
  /* 0.36.655 — Optional server-side data source for library-backed
   * dropdowns. When set, `options` (if also passed) is ignored — the
   * dropdown manages its own option list via the form-libraries API:
   *
   *   {libraryKey:'iso_country_codes', lang:'hy', labelColumn:'code'}
   *
   * Behavior:
   *   - On mount, fetches /meta once to get total_rows.
   *   - total_rows ≤ 300: fetches every row up front (`load-all`
   *     mode). Feels instant, exact same UX as an `options` array.
   *   - total_rows > 300: switches to `paginated` mode. Page 1 of
   *     100 rows fetches on first panel open; typing in the search
   *     box debounces (300ms) to a fresh server query; scrolling
   *     near the bottom of the list fetches the next page and
   *     appends. Accumulated rows are capped at ~1000 to keep
   *     memory bounded on 10k+ row libraries — the selected value
   *     is pinned so its chip never loses its label.
   *   - On mount, if `value` is set and its label isn't present
   *     in the current option set, /hydrate resolves it in one call.
   *
   * When `serverSource` is set, the inline-expansion (radio-group)
   * branch is skipped — we don't have the count synchronously and a
   * library reference always renders as the dropdown UI.
   *
   * Shape:
   *   libraryKey (required)  — form-library key (e.g. 'iso_country_codes')
   *   lang        (optional) — language code for translated labels
   *   labelColumn (optional) — override which column shows as label
   */
  serverSource = null,
  /* 0.46.28 - Open the list as soon as the dropdown appears, with the
   * search field focused, and tell the caller when it closes (a pick,
   * Escape or a click outside). For a dropdown shown on demand beside
   * a button, such as the column picker of a field in a card. */
  autoOpen = false,
  onClose = null,
  /* 0.49.10 - A small picker that keeps its own look (the form editor's
     Move to): triggerClassName draws the trigger instead of the Henaket
     field (the class brings its own chevron), and panelMinWidth lets the
     menu be wider than the trigger, kept inside the window. An option
     with disabled: true is shown dimmed and cannot be picked. */
  triggerClassName = null,
  panelMinWidth = 0,
}) {
  // 0.36.278 — Inline expansion: when the option count is at or below
  // the EFFECTIVE threshold for the current route (1 inside /admin/*,
  // the platform setting elsewhere — default 7), short-circuit the
  // dropdown UI and render a FormRadioGroup instead. Admin filter
  // toolbars / settings rows / table pickers therefore keep their
  // compact dropdown UI regardless of the platform setting.
  //
  // The `inlineThreshold` prop, when set, overrides the route check
  // entirely — used by the form preview to render citizen behavior
  // from within the admin area.
  //
  // Conditions:
  //   - Not explicitly disabled by the caller
  //   - effective threshold > 0 (admin can set platform to 0 to
  //     disable globally; admin context's hard-coded 0 means
  //     auto-expansion is off in admin area regardless)
  //   - options.length > 0 (an empty dropdown shouldn't render an
  //     orphan radio group with nothing in it)
  //   - options.length <= effective threshold
  const inlineThreshold = inlineThresholdProp !== undefined
    ? inlineThresholdProp
    : getEffectiveInlineThreshold();
  // 0.36.655 — serverSource can't be inline-expanded: total row count
  // isn't known synchronously, and a library reference is always meant
  // to render as the dropdown UI regardless of size.
  if (
    !disableAutoExpand &&
    !serverSource &&
    inlineThreshold > 0 &&
    options.length > 0 &&
    options.length <= inlineThreshold
  ) {
    return (
      <FormRadioGroup
        value={value ?? ''}
        onChange={onChange}
        options={options}
        name={name || id || `radio-group-${Math.random().toString(36).slice(2, 8)}`}
        disabled={disabled}
        ariaInvalid={ariaInvalid}
      />
    );
  }
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  // Whether the option-row magenta border is visible. Inverted to
  // false when the search input has focus, so the two indicators
  // never coexist.
  const [optionHighlightActive, setOptionHighlightActive] = useState(true);
  // 0.36.167 - panel-position computed on open. 'down' (default) places
  // the list below the trigger; 'up' places it above when there isn't
  // enough room below. Inline max-height clamps the panel to whatever
  // space is actually available in the chosen direction (or 320px hard
  // cap, whichever is smaller).
  const [panelDirection, setPanelDirection] = useState('down');
  const [panelMaxHeight, setPanelMaxHeight] = useState(320);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  // 0.36.457 — Panel is portaled to document.body, so it's no longer
  // inside containerRef. Outside-click detection needs BOTH refs to
  // know a click on an option inside the portaled panel isn't outside.
  const panelRef = useRef(null);
  // 0.45.33 - Search highlight in the open panel.
  useSearchHighlight(panelRef, open ? query : '', highlightMatches);

  // 0.36.655 — Server-source state. When `serverSource` is set, the
  // dropdown manages its own option list via the form-libraries API
  // instead of consuming the `options` prop. `serverMode` is null
  // until /meta answers; then 'load-all' (≤300 rows) or 'paginated'
  // (>300 rows). All state guarded by `serverSource` so passing null
  // (the default) means zero API calls fire and the component
  // behaves EXACTLY as before this ship.
  const [serverMode, setServerMode] = useState(null);
  const [serverOptions, setServerOptions] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  // Debounce timer for search → server refetch. Ref so effect
  // cleanups can cancel a pending fire.
  const searchDebounceRef = useRef(null);
  // Row cap: paginated mode keeps up to ~1000 rows in memory. Once
  // exceeded, oldest rows drop but the selected value stays pinned.
  const ROW_CAP = 1000;
  const SERVER_PER_PAGE = 100;
  const SERVER_THRESHOLD = 300;
  const SEARCH_DEBOUNCE_MS = 300;

  // Extract stable values from serverSource so effect deps don't
  // refire on every render if parent passes a fresh object.
  const serverKey = serverSource?.libraryKey || null;
  const serverLang = serverSource?.lang || null;
  const serverLabelCol = serverSource?.labelColumn || null;
  // 0.36.661 — Optional `valueColumn` on serverSource. When set, the
  // option's value is taken from the named column of each row
  // (instead of the default id/code/business_id fallback). Lets a
  // dropdown store, say, class numbers as values so a downstream
  // cascade filter can match against them directly.
  //
  // Limitation: /hydrate looks values up by the library's PK, so if
  // the caller uses a non-PK valueColumn, pre-selected values won't
  // resolve to labels on mount. For this NCL testing page that's
  // fine — filter values live only in local state. A follow-up ship
  // will extend /hydrate to accept a matching key_column.
  const serverValueCol = serverSource?.valueColumn || null;
  // 0.36.659 — Multi-column label. When `labelColumns` is set on
  // serverSource, `_rowToOption` joins the named columns with
  // `labelSeparator` (default " ") instead of using labelColumn.
  // Hydrate passes label_columns to the backend so preselected
  // values get the joined label too.
  const serverLabelCols = Array.isArray(serverSource?.labelColumns)
    ? serverSource.labelColumns.filter(c => c && String(c).trim())
    : null;
  const serverLabelSep = (serverSource?.labelSeparator != null)
    ? String(serverSource.labelSeparator) : ' ';
  // 0.36.660 — Identity-stable key. See HenaketMultiDropdown.
  const serverLabelColsKey = serverLabelCols
    ? serverLabelCols.join('|') : '';
  // 0.36.658 — Cascade filter parameters. When `filterColumn` is set,
  // rows are narrowed server-side to those where filterColumn IN
  // filterValues. Filter values change (e.g. parent dropdown changes)
  // trigger a refetch just like the query does.
  const serverFilterCol = serverSource?.filterColumn || null;
  const serverFilterValues = Array.isArray(serverSource?.filterValues)
    ? serverSource.filterValues : null;
  const serverFilterKey = serverFilterCol
    ? (serverFilterValues || []).map(String).sort().join(',')
    : '';

  // Helper: map an API row into the {value, label} option shape.
  // Uses `labelColumns` (joined) if set, else `labelColumn`, else a
  // smart default. Uses `valueColumn` if set for the value.
  const _rowToOption = useCallback((row, labelColOverride) => {
    // 0.36.661 — Explicit valueColumn takes precedence over the
    // id/code/business_id fallback chain.
    let val;
    if (serverValueCol && row[serverValueCol] != null) {
      val = row[serverValueCol];
    } else {
      val = row.id != null ? row.id : (row.code != null ? row.code : row.business_id);
    }
    // 0.36.659 — Multi-column label. Join non-blank column values
    // with labelSeparator. Falls back to single-column if none of
    // the specified columns has a usable value.
    if (serverLabelCols && serverLabelCols.length > 0) {
      const parts = serverLabelCols
        .map(c => (row[c] == null ? '' : String(row[c])))
        .map(s => s.trim())
        .filter(s => s !== '');
      if (parts.length > 0) {
        return { value: val, label: parts.join(serverLabelSep) };
      }
      // fall through to single-column defaults
    }
    let label = null;
    if (labelColOverride && row[labelColOverride] != null) {
      label = row[labelColOverride];
    } else if (row.name != null) {
      label = row.name;
    } else if (row.label != null) {
      label = row.label;
    } else {
      label = String(val);
    }
    return { value: val, label: String(label) };
  // 0.36.660 — Stable dep key. See serverLabelColsKey.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLabelColsKey, serverLabelSep, serverValueCol]);

  // /meta on mount → decide mode.
  useEffect(() => {
    if (!serverKey) return undefined;
    let cancelled = false;
    setServerLoading(true);
    setServerError('');
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/meta`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(meta => {
        if (cancelled) return;
        setServerTotal(meta.total_rows || 0);
        setServerMode(
          (meta.total_rows || 0) <= SERVER_THRESHOLD ? 'load-all' : 'paginated',
        );
      })
      .catch(err => {
        if (cancelled) return;
        setServerError(String(err.message || err));
        setServerLoading(false);
      });
    return () => { cancelled = true; };
  }, [serverKey]);

  // load-all mode: fetch every row once.
  useEffect(() => {
    if (!serverKey || serverMode !== 'load-all') return undefined;
    let cancelled = false;
    setServerLoading(true);
    const params = new URLSearchParams();
    if (serverLang) params.set('lang', serverLang);
    // 0.36.658 — Cascade filter forwarded to backend.
    if (serverFilterCol) {
      params.set('filter_column', serverFilterCol);
      params.set('filter_values',
        (serverFilterValues || []).map(String).join(','));
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        setServerOptions((data.rows || []).map(r => _rowToOption(r, serverLabelCol)));
        setServerHasMore(false);
      })
      .catch(err => {
        if (cancelled) return;
        setServerError(String(err.message || err));
      })
      .finally(() => {
        if (!cancelled) setServerLoading(false);
      });
    return () => { cancelled = true; };
  }, [serverKey, serverMode, serverLang, serverLabelCol,
      serverFilterCol, serverFilterKey, _rowToOption]);

  // paginated mode: fetch page 1 when search query changes (debounced)
  // or on initial mode assignment.
  useEffect(() => {
    if (!serverKey || serverMode !== 'paginated') return undefined;
    // Cancel any pending debounce.
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    let cancelled = false;
    const trimmedQuery = query.trim();
    const doFetch = () => {
      setServerLoading(true);
      setServerError('');
      const params = new URLSearchParams({
        page: '1',
        per_page: String(SERVER_PER_PAGE),
      });
      if (serverLang) params.set('lang', serverLang);
      if (trimmedQuery) params.set('q', trimmedQuery);
      // 0.36.658 — Cascade filter forwarded to backend.
      if (serverFilterCol) {
        params.set('filter_column', serverFilterCol);
        params.set('filter_values',
          (serverFilterValues || []).map(String).join(','));
      }
      fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(data => {
          if (cancelled) return;
          const opts = (data.rows || []).map(r => _rowToOption(r, serverLabelCol));
          setServerOptions(opts);
          setServerPage(1);
          setServerHasMore(opts.length < (data.total || 0));
        })
        .catch(err => {
          if (cancelled) return;
          setServerError(String(err.message || err));
        })
        .finally(() => {
          if (!cancelled) setServerLoading(false);
        });
    };
    // Debounce only for query changes; initial mode transition should
    // fire immediately.
    if (trimmedQuery) {
      searchDebounceRef.current = setTimeout(doFetch, SEARCH_DEBOUNCE_MS);
    } else {
      doFetch();
    }
    return () => {
      cancelled = true;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [serverKey, serverMode, serverLang, serverLabelCol, query,
      serverFilterCol, serverFilterKey, _rowToOption]);

  // Infinite scroll: on scroll near the bottom of the list, fetch
  // the next page and append. Only in paginated mode, and only when
  // there's more to load.
  const _handleListScroll = useCallback((e) => {
    if (serverMode !== 'paginated' || !serverHasMore || serverLoading) return;
    const el = e.currentTarget;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom) return;
    const nextPage = serverPage + 1;
    setServerLoading(true);
    const params = new URLSearchParams({
      page: String(nextPage),
      per_page: String(SERVER_PER_PAGE),
    });
    if (serverLang) params.set('lang', serverLang);
    const trimmedQuery = query.trim();
    if (trimmedQuery) params.set('q', trimmedQuery);
    // 0.36.658 — Cascade filter forwarded to backend.
    if (serverFilterCol) {
      params.set('filter_column', serverFilterCol);
      params.set('filter_values',
        (serverFilterValues || []).map(String).join(','));
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        const newOpts = (data.rows || []).map(r => _rowToOption(r, serverLabelCol));
        setServerOptions(prev => {
          const combined = [...prev, ...newOpts];
          // Row cap: drop oldest pages if we're over. Keep the
          // currently-selected value pinned so its chip doesn't
          // lose its label.
          if (combined.length > ROW_CAP) {
            const overflow = combined.length - ROW_CAP;
            const trimmed = combined.slice(overflow);
            const selectedIdx = combined.findIndex(o => o.value === value);
            if (selectedIdx >= 0 && selectedIdx < overflow) {
              // Selected fell off; re-insert at the front.
              return [combined[selectedIdx], ...trimmed];
            }
            return trimmed;
          }
          return combined;
        });
        setServerPage(nextPage);
        const totalLoaded = (serverOptions.length + newOpts.length);
        setServerHasMore(totalLoaded < (data.total || 0));
      })
      .catch(err => {
        setServerError(String(err.message || err));
      })
      .finally(() => setServerLoading(false));
  }, [serverKey, serverMode, serverLang, serverLabelCol, serverPage,
      serverHasMore, serverLoading, serverOptions.length, query, value,
      _rowToOption]);

  // Hydrate: if `value` is set and its label isn't in loaded options,
  // resolve it via /hydrate so the trigger shows a real label instead
  // of raw ID. Fires once per (serverKey, value, mode) combo.
  useEffect(() => {
    if (!serverKey || value == null || value === '' || !serverMode) return;
    const already = serverOptions.find(o => o.value === value);
    if (already) return;
    let cancelled = false;
    const params = new URLSearchParams({ keys: String(value) });
    if (serverLang) params.set('lang', serverLang);
    // 0.36.659 — Multi-column label: hydrate passes label_columns
    // + label_separator so the backend joins the label the same way
    // rows are labelled.
    if (serverLabelCols && serverLabelCols.length > 0) {
      params.set('label_columns', serverLabelCols.join(','));
      params.set('label_separator', serverLabelSep);
    } else if (serverLabelCol) {
      params.set('label_column', serverLabelCol);
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/hydrate?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        const item = (data.items || [])[0];
        if (!item) return;
        // Pin as an extra option so selectedOption resolves and the
        // trigger shows the label. The main options list may or may
        // not include this row depending on the current page/search
        // state — either way the chip stays populated.
        setServerOptions(prev =>
          prev.find(o => o.value === item.key)
            ? prev
            : [{ value: item.key, label: item.label }, ...prev]
        );
      })
      .catch(() => { /* soft-fail: leave value as its raw string */ });
    return () => { cancelled = true; };
  }, [serverKey, serverMode, value, serverLang, serverLabelCol, serverOptions]);

  // 0.36.655 — Effective options: server-managed list when serverSource
  // is set, otherwise the caller's `options` prop. Everything below
  // (selectedOption / filtered / openPanel / etc.) uses this so the
  // rest of the component doesn't need to know which mode it's in.
  const effectiveOptions = serverSource ? serverOptions : options;

  const selectedOption = useMemo(
    () => effectiveOptions.find(o => o.value === value) || null,
    [effectiveOptions, value],
  );

  const filtered = useMemo(() => {
    // In paginated server mode the backend already filtered by `q`,
    // so client-side filtering would double-filter. Return as-is.
    if (serverSource && serverMode === 'paginated') return effectiveOptions;
    if (!query.trim()) return effectiveOptions;
    const q = normalize(query);
    /* 0.47.85 - An option may carry `searchText`: the full text the
       search looks in, when its label is shortened for display. */
    return effectiveOptions.filter(o => normalize(o.searchText != null ? o.searchText : o.label).includes(q));
  }, [effectiveOptions, query, serverSource, serverMode]);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setQuery('');
    const idx = effectiveOptions.findIndex(o => o.value === value);
    setHighlightIndex(idx >= 0 ? idx : 0);
    setOptionHighlightActive(true);
    setOpen(true);
  }, [disabled, effectiveOptions, value]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setQuery('');
    if (typeof onClose === 'function') onClose();
  }, [onClose]);
  // 0.46.28 - autoOpen: open once, on mount, and focus the search.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpened.current) return;
    autoOpened.current = true;
    openPanel();
  }, [autoOpen, openPanel]);
  useEffect(() => {
    if (!autoOpen || !open) return undefined;
    // The panel is portaled and measured first: focus once it is drawn.
    let tries = 0;
    let t = null;
    const focus = () => {
      if (searchInputRef.current) { searchInputRef.current.focus(); return; }
      if (tries++ < 10) t = setTimeout(focus, 30);
    };
    t = setTimeout(focus, 30);
    return () => clearTimeout(t);
  }, [autoOpen, open]);

  const select = useCallback((v) => {
    const opt = (options || []).find(o => o && o.value === v);
    if (opt && opt.disabled) return;
    onChange?.(v);
    closePanel();
  }, [onChange, closePanel, options]);

  // Click-outside closes the panel.

  // 0.36.457 — Portal-render position (fixed in viewport coords).
  // Needed so the panel can escape parent `overflow: auto` containers
  // (specifically `.tableScrollWrapper` in PageTable, which has
  // overflow-x: auto and per CSS spec that implicitly forces
  // overflow-y: auto — so the absolute-positioned panel was clipped
  // by the table wrapper when tables had few rows). See ship 0.36.457
  // notes for full trace.
  const [panelPos, setPanelPos] = useState({
    left: 0, width: 0, top: null, bottom: null,
  });

  // 0.36.167 - measure available space below/above the trigger and
  // decide direction + max-height when the panel opens. Also re-measures
  // on window resize while open (so flips correctly if the user resizes
  // mid-interaction).
  const _measurePanel = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const BUFFER = 8;
    const MAX = 320;
    const MIN = 120;
    // 0.45.48 - The trigger's focus ring is a 3px shadow outside it, so
    // the panel starts 2px beyond the ring (was 2px from the trigger,
    // which covered the ring's bottom edge while the panel was open).
    const FOCUS_RING = 3;
    const GAP = FOCUS_RING + 2;
    const spaceBelow = viewportH - rect.bottom - BUFFER;
    const spaceAbove = rect.top - BUFFER;
    let direction, maxHeight;
    if (spaceBelow >= MAX) {
      direction = 'down';
      maxHeight = MAX;
    } else if (spaceAbove > spaceBelow) {
      direction = 'up';
      maxHeight = Math.min(MAX, Math.max(MIN, spaceAbove));
    } else {
      direction = 'down';
      maxHeight = Math.max(MIN, spaceBelow);
    }
    setPanelDirection(direction);
    setPanelMaxHeight(maxHeight);
    // 0.36.457 — Viewport-relative coords for portal-rendered fixed
    // positioning. Down: panel top edge sits at trigger.bottom + gap.
    // Up: panel bottom edge sits at (viewportH - trigger.top) + gap
    // measured from the viewport BOTTOM (CSS `bottom` value), which
    // is easier than computing panel_top = trigger.top - height when
    // height is dynamic (maxHeight is an upper bound, actual content
    // may be shorter).
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const width = Math.min(Math.max(rect.width, panelMinWidth || 0), viewportW - 2 * BUFFER);
    const left = Math.max(BUFFER, Math.min(rect.left, viewportW - width - BUFFER));
    setPanelPos({
      left,
      width,
      top: direction === 'down' ? rect.bottom + GAP : null,
      bottom: direction === 'up' ? viewportH - rect.top + GAP : null,
    });
  }, [panelMinWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    _measurePanel();
    const onResize = () => _measurePanel();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, _measurePanel]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      // 0.36.457 — Panel is portaled to document.body, so a click
      // on an option or the search input inside the panel is NOT
      // inside containerRef. Consult panelRef too.
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inContainer && !inPanel) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closePanel]);

  // Scroll the highlighted option into view. 0.35.43 — switched
  // from manual scrollTop math on the inner list to scrollIntoView
  // on the option, with the PANEL as the scroll container. The
  // panel has `scroll-padding-top: 45px` to reserve the sticky
  // search-bar area; browsers honor that padding when computing
  // scrollIntoView({block: 'nearest'}), so an option scrolled to
  // the top of the visible area lands BELOW the sticky search
  // rather than underneath it. Resolves the bug where the topmost
  // visible option after keyboard scrolling was hidden behind the
  // search bar.
  useEffect(() => {
    if (!open || !listRef.current) return undefined;
    const raf = requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const el = list.children[highlightIndex];
      if (!el) return;
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [highlightIndex, open]);

  useEffect(() => {
    if (highlightIndex >= filtered.length) {
      setHighlightIndex(0);
    }
  }, [filtered.length, highlightIndex]);

  // ============== Focus-state transitions ==============

  const handleSearchFocus = () => {
    // User clicked / tabbed into search → search is the focused
    // affordance now. Hide the option highlight.
    setOptionHighlightActive(false);
  };

  const handleOptionMouseEnter = (idx) => {
    // Mouse interaction with options → blur the search input so
    // its focus ring disappears, bring the option highlight here.
    if (document.activeElement === searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setOptionHighlightActive(true);
    setHighlightIndex(idx);
  };

  const moveHighlight = (direction) => {
    setOptionHighlightActive(true);
    setHighlightIndex(i =>
      direction === 'down'
        ? Math.min(i + 1, Math.max(filtered.length - 1, 0))
        : Math.max(i - 1, 0)
    );
  };

  const commitHighlighted = () => {
    const opt = filtered[highlightIndex];
    if (opt) select(opt.value);
  };

  // ============== Keyboard handlers ==============
  //
  // Trigger handles keys when (a) panel is closed and user opens it,
  // OR (b) panel is open and focus is still on the trigger (the user
  // opened by mouse-click and hasn't moved focus elsewhere).
  // Search handles keys when focus is in the search input.

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openPanel();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight('up');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitHighlighted();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight('up');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitHighlighted();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    } else if (e.key === 'Tab') {
      closePanel();
    }
  };

  const triggerLabel = selectedOption?.label || placeholder || '';
  const showingPlaceholder = !selectedOption;

  // .triggerOpen suppresses the magenta focus ring on the trigger
  // when the panel is open — the option highlight inside the panel
  // is the focus affordance in this state, so a ring on the trigger
  // would be a second focus indicator.
  const triggerClass = triggerClassName ? triggerClassName : [
    styles.trigger,
    open && styles.triggerOpen,
    showingPlaceholder && styles.triggerPlaceholder,
    error && 'inputError',
  ].filter(Boolean).join(' ');
  // 0.36.560 — Inline style fail-safe for the error border. Same
  // rationale as the checkbox in AdminLibraryDetail — inline style
  // beats any specificity war and guarantees admin sees the red
  // border, regardless of what upstream CSS the trigger inherits.
  const triggerStyle = error
    ? { border: '2px solid var(--color-error-dark)',
        boxShadow: '0 0 0 2px var(--color-error-light)' }
    : undefined;

  return (
    <div className={styles.container} ref={containerRef}
         style={triggerClassName ? { display: 'inline-block', width: 'auto' } : undefined}>
      <button
        type="button"
        id={id}
        name={name}
        className={triggerClass}
        style={triggerStyle}
        onClick={(e) => {
          // 0.36.28 — Explicitly focus the trigger on mouse click.
          // Safari and Firefox on macOS don't auto-focus buttons
          // when clicked (only Chrome/Edge do). Without this, the
          // .triggerOpen:focus rule that draws the magenta ring
          // never fires for mouse users in those browsers — the
          // trigger looks unfocused even though we're driving the
          // panel from it.
          e.currentTarget.focus();
          if (open) closePanel(); else openPanel();
        }}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={ariaInvalid}
        aria-required={required}
      >
        {triggerClassName ? triggerLabel : (
          <>
            <span className={styles.triggerLabel}>{triggerLabel}</span>
            <ChevronDown />
          </>
        )}
      </button>

      {/* 0.36.457 — Panel is now portaled to document.body with
          position: fixed and viewport coords from panelPos. See
          `_measurePanel` for coord computation. Escapes parent
          overflow containers like PageTable's .tableScrollWrapper
          which was clipping the panel in short tables. */}
      {open && createPortal(
        <div
          ref={panelRef}
          className={panelDirection === 'up' ? `${styles.panel} ${styles.panelUp}` : styles.panel}
          style={{
            ...(highlightColor ? { '--search-highlight-color': highlightColor } : null),
            position: 'fixed',
            left: `${panelPos.left}px`,
            width: `${panelPos.width}px`,
            top: panelPos.top !== null ? `${panelPos.top}px` : 'auto',
            bottom: panelPos.bottom !== null ? `${panelPos.bottom}px` : 'auto',
            right: 'auto',
            maxHeight: `${panelMaxHeight}px`,
          }}
          role="dialog"
        >
          {/* 0.36.277 — Henaket rule, unchanged: show the search
            * input only when the list has more than 15 items. Below
            * that threshold, the entire list is visible at once and
            * search adds friction without benefit.
            *
            * Structure overhaul (0.36.277): search is now a SELF-
            * CONTAINED bordered box that sits above the listBox with
            * a 2 px flex `gap`. Visually distinct elements — no
            * sticky/scroll overlap.
            *
            * The icon affix now sits on the RIGHT with a 1 px vertical
            * separator before it (was: icon on the left, absolute-
            * positioned). Layout: [ input | separator | 🔍 ].
            */}
          {/* 0.36.278 / 0.36.281 — Search visibility honors (in
              priority order): the per-element override prop, then
              the platform search threshold. 0 hides the search
              field entirely; any positive value shows it when
              options.length > threshold. */}
          {(() => {
            // 0.36.655 — In paginated server mode the search input
            // is ALWAYS visible: the user can't rely on scrolling
            // through 10k rows and the input is how they narrow the
            // set. In load-all server mode the threshold logic
            // applies to the loaded row count, same as options mode.
            if (serverSource && serverMode === 'paginated') return true;
            const t = searchThresholdProp !== undefined
              ? searchThresholdProp
              : getSearchThreshold();
            return t > 0 && effectiveOptions.length > t;
          })() && (
            <div className={styles.searchBox}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                autoComplete="off"
              />
              <div className={styles.searchSeparator} />
              <div className={styles.searchIconWrap}>
                <SearchIcon />
              </div>
            </div>
          )}

          {/* 0.36.277 — listBox is the scrollable container. Empty
              state lives inside it so the bordered slot is always
              rendered, even when the list filters down to zero.
              0.36.655 — Server-source paginated mode wires the scroll
              handler onto the listBox itself (the actual scrollable
              element) so scroll-near-bottom triggers infinite load.
              Loading + error indicators sit at the top when active. */}
          <div
            className={styles.listBox}
            onScroll={serverSource ? _handleListScroll : undefined}
          >
            {serverSource && serverError && (
              <div className={styles.empty} style={{ color: 'var(--color-error)' }}>
                {serverError}
              </div>
            )}
            {filtered.length === 0 && !serverLoading ? (
              <div className={styles.empty}>{noMatchesLabel}</div>
            ) : (
              <ul
                ref={listRef}
                className={styles.list}
                role="listbox"
              >
              {filtered.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHighlighted = idx === highlightIndex;
                // Magenta border only when optionHighlightActive AND
                // this row matches highlightIndex. When search has
                // focus, no row shows the border.
                const showBorder = optionHighlightActive && isHighlighted;
                const optionClass = [
                  styles.option,
                  isSelected && styles.optionSelected,
                  showBorder && !opt.disabled && styles.optionHighlighted,
                  opt.disabled && styles.optionDisabled,
                ].filter(Boolean).join(' ');
                return (
                  <li
                    key={opt.value}
                    className={optionClass}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    onMouseEnter={() => handleOptionMouseEnter(idx)}
                    /* 0.36.30 — Prevent the default mousedown focus
                     * shift so the trigger button KEEPS focus when
                     * the user clicks an option. Without this, the
                     * native mousedown moves focus to the LI (or
                     * document.body since LI isn't focusable), and
                     * after select()+closePanel() runs there's no
                     * focused element → no magenta ring, looks like
                     * the dropdown "lost" focus. preventDefault
                     * blocks the focus shift without affecting the
                     * click event itself. */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(opt.value)}
                  >
                    {opt.label}
                  </li>
                );
              })}
              {/* 0.36.655 — In paginated mode, small "Loading more..."
                  strip at the bottom of the list while the next page
                  is in flight. Non-interactive; the scroll handler
                  triggers the fetch. */}
              {serverSource && serverMode === 'paginated' && serverLoading && (
                <li className={styles.empty} style={{ padding: 8, opacity: 0.7 }}>
                  Loading…
                </li>
              )}
            </ul>
          )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
